import { useQuery } from '@tanstack/react-query';
import { REGION_CONFIG } from '../config/region';  // Import REGION_CONFIG
import api from './api';
import { API_BASE_URL } from '../utils/constants';  // Use centralized API config

// Request queue to prevent duplicate simultaneous requests
const requestQueue = new Map();

// Abort controllers for canceling requests
const abortControllers = new Map();

// Helper to get auth token from localStorage - FIXED to match your token key
const getAuthToken = () => {
  try {
    // ✅ Match the token key from api.js
    const token = localStorage.getItem('venue_app_token') || 
                  localStorage.getItem('token') || 
                  localStorage.getItem('authToken') || 
                  localStorage.getItem('auth_token') ||
                  sessionStorage.getItem('venue_app_token') ||
                  sessionStorage.getItem('token') ||
                  sessionStorage.getItem('authToken');
    
    if (token) {
      console.log('✅ Auth token found:', token.substring(0, 20) + '...');
    } else {
      console.log('⚠️ No auth token found in storage');
    }
    
    return token;
  } catch (error) {
    console.error('❌ Error getting auth token:', error);
    return null;
  }
};

// Helper to create unique request key
const createRequestKey = (endpoint, params) => {
  return `${endpoint}_${JSON.stringify(params)}`;
};

// ✅ Helper to transform event images
const transformEventData = (event) => {
  if (!event) return event;
  
  return {
    ...event,
    // ✅ Ensure images array exists and is properly formatted
    images: event.images && Array.isArray(event.images) ? event.images : [],
    // ✅ Fallback to first image if available
    image: event.images?.[0] || event.imageUrl || event.image,
    // ✅ Preserve all original fields
    EventName: event.EventName || event.eventName || event.title,
    Evendescription: event.Evendescription || event.description,
    EventStartsAt: event.EventStartsAt || event.eventStartsAt,
    EventEndsAt: event.EventEndsAt || event.eventEndsAt,
    EventCategory: event.EventCategory || event.eventCategory,
    EventPricefrom: event.EventPricefrom || event.eventPriceFrom,
    EventPriceMax: event.EventPriceMax || event.eventPriceMax,
    EventCurrency: event.EventCurrency || event.eventCurrency || 'AED',
    EventAgeRestriction: event.EventAgeRestriction || event.eventAgeRestriction,
    Eventtypsdisplayname: event.Eventtypsdisplayname || event.eventType,
    Eventtype: event.Eventtype || event.eventType
  };
};

// ✅ Helper to transform events array
const transformEventsArray = (events) => {
  if (!Array.isArray(events)) return [];
  return events.map(transformEventData);
};

// Helper to fetch with timeout and abort capability with authentication
const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  
  try {
    // Get auth token
    const token = getAuthToken();
    
    // Prepare headers with optional authentication
    const headers = {
      'Content-Type': 'application/json',
      'x-region': REGION_CONFIG.region, // ✅ Add region header like api.js does
      ...options.headers,
    };
    
    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Log request details for debugging
    console.log('🔑 Making authenticated request:', {
      url: url.substring(0, 100) + (url.length > 100 ? '...' : ''),
      hasAuth: !!token,
      method: options.method || 'GET',
      region: REGION_CONFIG.region
    });
    
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers
    });
    
    clearTimeout(id);
    
    // Handle different error statuses
    if (!response.ok) {
      if (response.status === 401) {
        console.error('🚫 Unauthorized (401) - Token missing, invalid, or expired');
        console.error('Token in storage:', !!token);
        console.error('Token preview:', token ? token.substring(0, 20) + '...' : 'none');
        // Optionally clear invalid token and redirect
        // localStorage.removeItem('venue_app_token');
        // window.location.href = '/login';
      } else if (response.status === 403) {
        console.error('🚫 Forbidden (403) - Insufficient permissions');
      } else if (response.status === 404) {
        console.error('❌ Not Found (404) - Resource not found');
      } else if (response.status === 500) {
        console.error('💥 Server Error (500) - Internal server error');
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
  } catch (error) {
    clearTimeout(id);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - server took too long to respond');
    }
    
    if (error.message === 'Failed to fetch') {
      throw new Error('Cannot connect to server. Please ensure the backend server is running on port 4000.');
    }
    
    throw error;
  }
};

// Optimized fetch function with request deduplication
const optimizedFetch = async (endpoint, params = {}) => {
  const requestKey = createRequestKey(endpoint, params);
  
  // Check if same request is already in progress
  if (requestQueue.has(requestKey)) {
    console.log('📦 Reusing pending request:', requestKey);
    return requestQueue.get(requestKey);
  }
  
  // Cancel any previous request for this endpoint
  if (abortControllers.has(endpoint)) {
    abortControllers.get(endpoint).abort();
  }
  
  const controller = new AbortController();
  abortControllers.set(endpoint, controller);
  
  // Create the promise
  const requestPromise = (async () => {
    try {
      const queryParams = new URLSearchParams();
      
      // Only add non-empty parameters
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
      
      const url = `${API_BASE_URL}${endpoint}?${queryParams.toString()}`;
      
      console.log('🔄 Fetching from:', url.substring(0, 150) + (url.length > 150 ? '...' : ''));
      
      const response = await fetchWithTimeout(
        url,
        { 
          signal: controller.signal,
          method: 'GET'
        },
        10000
      );
      
      const data = await response.json();
      
      // ✅ Transform event data to include images
      if (data?.data) {
        if (Array.isArray(data.data)) {
          data.data = transformEventsArray(data.data);
        } else {
          data.data = transformEventData(data.data);
        }
      }
      
      if (data?.events) {
        data.events = transformEventsArray(data.events);
      }
      
      console.log('✅ Data loaded successfully:', {
        endpoint: endpoint.substring(0, 50),
        dataLength: data?.data?.length || data?.events?.length || 0,
        hasImages: data?.data?.[0]?.images?.length > 0 || false
      });
      
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🔄 Request cancelled:', endpoint);
        return null;
      }
      console.error('❌ Fetch error:', error.message);
      throw error;
    } finally {
      requestQueue.delete(requestKey);
      abortControllers.delete(endpoint);
    }
  })();
  
  requestQueue.set(requestKey, requestPromise);
  
  return requestPromise;
};

export const fetchAllEvents = async (params = {}) => {
  const today = new Date().toISOString().split('T')[0];

  // Build params - only add upcoming filter if explicitly true
  const defaultParams = {
    country: REGION_CONFIG.country,
    region: REGION_CONFIG.apiRegion,
    page: params.page || 1,
    limit: params.limit || 100,
  };

  // Only add upcoming filter if explicitly set to true
  // When upcoming=false, we want to see ALL events including past ones
  if (params.upcoming === true) {
    defaultParams.upcoming = 'true';
    defaultParams.startDate = params.startDate || today;
    defaultParams.endDate = params.endDate || today;
  } else if (params.upcoming === false) {
    // Don't add upcoming filter - fetch all events
    defaultParams.upcoming = 'false';
  }

  if (params.search) defaultParams.search = params.search;
  if (params.Event_Category) defaultParams.Event_Category = params.Event_Category;
  if (params.category) defaultParams.category = params.category;
  if (params.event_type) defaultParams.event_type = params.event_type;
  if (params.minPrice !== undefined) defaultParams.minPrice = params.minPrice;
  if (params.maxPrice !== undefined) defaultParams.maxPrice = params.maxPrice;
  if (params.Event_Age_Restriction) defaultParams.Event_Age_Restriction = params.Event_Age_Restriction;
  if (params.isFree !== undefined) defaultParams.isFree = params.isFree;
  if (params.isPaid !== undefined) defaultParams.isPaid = params.isPaid;
  if (params.sortBy) defaultParams.sortBy = params.sortBy;

  try {
    console.log(`🎯 Fetching events with params:`, defaultParams);
    let result = await optimizedFetch('/events', defaultParams);

    if (!result) {
      throw new Error('Failed to fetch events');
    }
    
    console.log(`✅ Fetched ${result.data?.length || 0} events`);
    return result;
  } catch (error) {
    console.error('❌ Events API error:', error);
    throw error;
  }
};

// ✅ Updated: Fetch events by venue with image support
export const fetchEventsByVenue = async ({ accountName, upcoming = true }) => {
  if (!accountName) {
    throw new Error('Account name is required');
  }

  const endpoint = `/events/venue/${encodeURIComponent(accountName)}`;
  const result = await optimizedFetch(endpoint, {
    upcoming,
    country: REGION_CONFIG.country,
    region: REGION_CONFIG.apiRegion,
  });

  if (!result) {
    throw new Error('Failed to fetch venue events');
  }
  
  // ✅ Transform events data to include images
  if (result.data?.events) {
    result.data.events = transformEventsArray(result.data.events);
  }
  
  return result;
};

// ✅ Updated: Fetch single event by ID with image support
export const fetchEventById = async (eventId) => {
  if (!eventId) {
    throw new Error('Event ID is required');
  }

  const endpoint = `/events/${eventId}`;
  const result = await optimizedFetch(endpoint, { 
    region: REGION_CONFIG.apiRegion,
    country: REGION_CONFIG.country
  });

  if (!result) {
    throw new Error('Failed to fetch event');
  }
  
  // ✅ Transform single event data
  if (result.data) {
    result.data = transformEventData(result.data);
  }
  
  return result;
};

// ✅ Updated: Fetch upcoming events with image support
export const fetchUpcomingEvents = async (limit = 20) => {
  const result = await optimizedFetch('/events/upcoming', {
    limit,
    country: REGION_CONFIG.country,
    region: REGION_CONFIG.apiRegion,
  });

  if (!result) {
    throw new Error('Failed to fetch upcoming events');
  }
  
  // ✅ Transform events data
  if (result.data && Array.isArray(result.data)) {
    result.data = transformEventsArray(result.data);
  }
  
  return result;
};

// Legacy API object kept with added region and country params
export const eventsApi = {
  getAllEvents: async (params = {}) => {
    try {
      return await fetchAllEvents(params);
    } catch (error) {
      console.error('Error fetching events:', error);
      return {
        success: false,
        error: error.message,
        data: [],
        pagination: {
          currentPage: params.page || 1,
          totalCount: 0,
          totalPages: 0,
        }
      };
    }
  },

  // ✅ Updated with image support
  getEventsByVenue: async (accountName, upcoming = true) => {
    try {
      const result = await fetchEventsByVenue({ accountName, upcoming });
      return {
        success: true,
        data: result
      };
    } catch (error) {
      console.error('Error fetching venue events:', error);
      return {
        success: false,
        error: error.message,
        data: { events: [], eventCount: 0 }
      };
    }
  },

  // ✅ Updated with image support
  getEventById: async (eventId) => {
    try {
      return await fetchEventById(eventId);
    } catch (error) {
      console.error('Error fetching event:', error);
      return {
        success: false,
        error: error.message,
        data: null,
      };
    }
  },

  // ✅ Updated with image support
  getUpcomingEvents: async (limit = 20) => {
    try {
      return await fetchUpcomingEvents(limit);
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      return {
        success: false,
        error: error.message,
        data: [],
        count: 0,
      };
    }
  },

  cancelAllRequests: () => {
    abortControllers.forEach(controller => {
      controller.abort();
    });
    abortControllers.clear();
    requestQueue.clear();
    console.log('🛑 All API requests cancelled');
  }
};

// ⭐ React Query hooks (use these in components)

// Hook for fetching all events
export const useAllEvents = (params = {}) => {
  return useQuery({
    queryKey: ['events', params],
    queryFn: () => fetchAllEvents(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook for fetching events by venue
export const useVenueEvents = (accountName, upcoming = true) => {
  return useQuery({
    queryKey: ['venue-events', accountName, upcoming],
    queryFn: () => fetchEventsByVenue({ accountName, upcoming }),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!accountName,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook for fetching single event
export const useEvent = (eventId) => {
  return useQuery({
    queryKey: ['event', eventId],
    queryFn: () => fetchEventById(eventId),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!eventId,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook for fetching upcoming events
export const useUpcomingEvents = (limit = 20) => {
  return useQuery({
    queryKey: ['upcoming-events', limit],
    queryFn: () => fetchUpcomingEvents(limit),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Export utility to manually set auth token (useful for testing)
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('venue_app_token', token); // ✅ Match api.js key
    console.log('✅ Auth token set successfully');
  } else {
    localStorage.removeItem('venue_app_token');
    console.log('🗑️ Auth token removed');
  }
};

// Export utility to check if user is authenticated
export const isAuthenticated = () => {
  const token = getAuthToken();
  return !!token;
};

// Clean up function to call on logout
export const cleanupAuth = () => {
  localStorage.removeItem('venue_app_token'); // ✅ Match api.js key
  localStorage.removeItem('token');
  localStorage.removeItem('authToken');
  localStorage.removeItem('auth_token');
  sessionStorage.removeItem('venue_app_token');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('authToken');
  eventsApi.cancelAllRequests();
  console.log('🧹 Auth cleanup completed');
};

export default eventsApi;
